/**
 * Dates, times and quantities as the screens write them.
 *
 * Every time is shown in the planner's own zone, which the API sends with each
 * day as `time_zone`, and never in the browser's: a dispatcher on a laptop set
 * to another zone still has to read the time a truck leaves. Everything goes
 * through `Intl.DateTimeFormat` with that zone, en-GB, on a 24 hour clock.
 *
 * A plain date (`YYYY-MM-DD`: a run's horizon, the day on screen) has no zone
 * at all, so it is formatted as UTC midnight in UTC, which can never shift it
 * to the day before.
 */
const LOCALE = "en-GB";

const clockFormats = new Map<string, Intl.DateTimeFormat>();

/** One formatter per zone, made once: a formatter is not cheap to build and a
 *  day page formats a few hundred times. */
function clockIn(timeZone: string): Intl.DateTimeFormat {
  let format = clockFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat(LOCALE, {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    clockFormats.set(timeZone, format);
  }
  return format;
}

interface WallClock {
  date: string;
  hour: number;
  minute: number;
}

function wallClock(iso: string, timeZone: string): WallClock {
  const parts: Record<string, string> = {};
  for (const part of clockIn(timeZone).formatToParts(new Date(iso))) parts[part.type] = part.value;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour),
    minute: Number(parts.minute),
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** `09:38`: the wall-clock time in the zone, seconds dropped as a clock does. */
export function formatTime(iso: string, timeZone: string): string {
  const { hour, minute } = wallClock(iso, timeZone);
  return `${pad(hour)}:${pad(minute)}`;
}

/** The time, and the day it falls on when that is not the day on screen: a
 *  trip that leaves at 23:10 is back at "01:05 next day", not at 01:05. */
export function formatClock(iso: string, timeZone: string, day: string): string {
  const offset = daysBetween(day, wallClock(iso, timeZone).date);
  const time = formatTime(iso, timeZone);
  if (offset === 0) return time;
  if (offset === 1) return `${time} next day`;
  if (offset === -1) return `${time} previous day`;
  return `${time} on ${formatShortDate(wallClock(iso, timeZone).date)}`;
}

/**
 * Where a time sits on the day's axis: wall-clock minutes from the local
 * midnight that starts `day`, below 0 or past 1440 for a neighbouring day.
 *
 * Wall-clock, because the axis is labelled in wall-clock hours. On the two
 * days a year the clocks change, a span across the change is drawn an hour
 * longer or shorter than it lasted; the times written on it stay right.
 */
export function minutesIntoDay(iso: string, timeZone: string, day: string): number {
  const { date, hour, minute } = wallClock(iso, timeZone);
  return daysBetween(day, date) * 1440 + hour * 60 + minute;
}

const longDate = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});
const shortDate = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

const utcMidnight = (date: string) => new Date(`${date}T00:00:00Z`);

/** `Thursday 1 October 2026`. en-GB puts a comma after the weekday; the
 *  artboards do not, so it is taken out. */
export function formatDate(date: string): string {
  return longDate.format(utcMidnight(date)).replace(",", "");
}

/** `1 Oct 2026`. */
export function formatShortDate(date: string): string {
  return shortDate.format(utcMidnight(date));
}

/** A plain date `days` later (or earlier), in UTC so no clock change moves it. */
export function addDays(date: string, days: number): string {
  const next = utcMidnight(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

/** A real calendar date in `YYYY-MM-DD`, not merely that shape: 2026-02-30
 *  is refused here rather than by the API. */
export function isCalendarDate(value: string | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = utcMidnight(value);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
}

/** Today's date at the plant, `2026-10-19`: the platform's days are UK days. */
export function londonToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** The Monday of the week (Monday to Sunday) a plain date falls in, the date
 *  that names a week's address. In UTC, like `addDays`. */
export function mondayOf(date: string): string {
  const weekday = utcMidnight(date).getUTCDay(); // 0 is Sunday
  return addDays(date, -((weekday + 6) % 7));
}

/** `7 h 46 min`, or `30 min` under an hour. */
export function formatDuration(hours: number): string {
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h === 0 ? `${m} min` : `${h} h ${m} min`;
}

// Whole numbers: litres are measured to the litre at best, and a forecast's decimals are noise
// (Ronald, 15 October 2026: Milk Flow showed 51,930.958 L).
const number = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const kilometres = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 });

/** `40,749`: a count or a quantity without its unit, for a figure tile
 *  whose unit is set apart. */
export function formatNumber(value: number): string {
  return number.format(value);
}

/** `19,013 L`. */
export function formatLitres(litres: number): string {
  return `${number.format(litres)} L`;
}

/** `509.9 km`. */
export function formatKilometres(km: number): string {
  return `${kilometres.format(km)} km`;
}

/**
 * The zone the times are in, as a dispatcher says it: "UK time", never the
 * IANA id the API sends ("Europe/London"). Another zone, for a planner sold
 * elsewhere, is named by the browser in words ("Central European Time").
 */
export function zoneName(timeZone: string): string {
  if (timeZone === "Europe/London") return "UK time";
  const part = new Intl.DateTimeFormat(LOCALE, { timeZone, timeZoneName: "longGeneric" })
    .formatToParts(new Date())
    .find((p) => p.type === "timeZoneName");
  return part?.value ?? "local time";
}
